/**
 * AvenBoxScene — scroll-locked 3D product reveal.
 *
 * Pin: GSAP pin:true (not CSS sticky) — proper scroll lock.
 * Sequence: front → back → side → ports tilt → front + xray
 * Lighting: 4-point studio setup.
 * Smoothing: lerp in AvenBoxLite useFrame, raw progress from onUpdate.
 */

import { lazy, Suspense, useRef } from 'react';
import { Canvas } from '@react-three/fiber';
import { motion, useMotionValue, useTransform, animate, type MotionValue } from 'framer-motion';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import type { AvenBoxScrollState } from './AvenBoxLite';

gsap.registerPlugin(ScrollTrigger, useGSAP);

const AvenBoxLite = lazy(() => import('./AvenBoxLite'));

// ── Rotation keyframe sequence ────────────────────────────────────────────
// Hold phases are 15–18% each. With end=+=1000%, 1% = 0.1 viewport.
// 15% hold = 1.5 viewports — model stays completely locked during holds.
// Transitions are narrow (6–8%) so rotation feels fast and decisive.
//
// HOLDS:  front 0.00–0.15 | back 0.23–0.40 | side 0.48–0.63 | ports 0.70–0.84 | xray 0.88–1.00
// TRANSITIONS between holds are the only time rotation moves.
function computeScrollState(p: number): AvenBoxScrollState {
    let rotY: number;
    if      (p < 0.15) rotY = 0;
    else if (p < 0.23) rotY = gsap.utils.mapRange(0.15, 0.23, 0,    0.5,  p);
    else if (p < 0.40) rotY = 0.5;
    else if (p < 0.48) rotY = gsap.utils.mapRange(0.40, 0.48, 0.5,  0.75, p);
    else if (p < 0.63) rotY = 0.75;
    else if (p < 0.70) rotY = gsap.utils.mapRange(0.63, 0.70, 0.75, 1.0,  p);
    else               rotY = 1.0;

    const HALF_PI = Math.PI / 2;
    let rotX: number;
    if      (p < 0.63) rotX = 0.04;
    else if (p < 0.70) rotX = gsap.utils.mapRange(0.63, 0.70, 0.04, -HALF_PI, p);
    else if (p < 0.84) rotX = -HALF_PI;
    else if (p < 0.88) rotX = gsap.utils.mapRange(0.84, 0.88, -HALF_PI, 0.04, p);
    else               rotX = 0.04;

    const vent =
        p < 0.23 ? 0 :
        p < 0.30 ? gsap.utils.clamp(0, 1, (p - 0.23) / 0.07) :
        p < 0.58 ? 1 :
        p < 0.65 ? gsap.utils.clamp(0, 1, 1 - (p - 0.58) / 0.07) : 0;

    const xray = p >= 0.88 ? gsap.utils.clamp(0, 1, (p - 0.88) / 0.12) : 0;

    return { rotY, rotX, xray, vent };
}

// ── Annotation ────────────────────────────────────────────────────────────
interface AnnotationData {
    // scroll progress range when this annotation is visible
    inAt:  number;
    outAt: number;
    side:  'left' | 'right';
    tag:   string;      // small uppercase tag
    label: string;      // main label
    body:  string;      // description
}

// Annotation windows cover the full hold phase with short fades at the edges,
// so the body text sits at full opacity for the whole time the model is locked.
const ANNOTATIONS: AnnotationData[] = [
    {
        inAt: 0.005, outAt: 0.15,   // front hold 0.00–0.15
        side: 'right',
        tag: 'Display',
        label: 'OLED STATUS',
        body: 'Direct model state readout. Status ring, mode label, battery level. No unlock screen — always visible.',
    },
    {
        inAt: 0.23, outAt: 0.40,    // back hold 0.23–0.40
        side: 'left',
        tag: 'Thermal',
        label: 'AVENCOOL',
        body: 'Active cooling with full-width exhaust. Copper thermal path keeps sustained workloads stable under the 157 TOPS envelope.',
    },
    {
        inAt: 0.48, outAt: 0.63,    // side hold 0.48–0.63
        side: 'right',
        tag: 'Side profile',
        label: 'ALUMINUM CHASSIS',
        body: '0.7mm wall. CNC-machined. Antenna lines at top and bottom. Side control button flush-mounted.',
    },
    {
        inAt: 0.70, outAt: 0.84,    // ports hold 0.70–0.84
        side: 'left',
        tag: 'Ports',
        label: 'DIRECT ACCESS',
        body: 'USB-C (left) and USB-A (right). Physical connection — no cloud handshake, no wireless pairing dependency.',
    },
    {
        inAt: 0.88, outAt: 1.00,    // xray hold 0.88–1.00
        side: 'right',
        tag: 'X-ray',
        label: 'FULL STACK',
        body: '6 layers: Shell / Chassis / PCB / AvenCore / Battery / Thermal. Every component in view — nothing hidden.',
    },
];

function Annotation({
    smoothProgress,
    inAt, outAt, side, tag, label, body,
}: AnnotationData & { smoothProgress: MotionValue<number> }) {
    // Short fade-in, tiny fade-out. Full-opacity window covers ~90% of the hold.
    const fadeIn  = 0.025;
    const fadeOut = 0.02;
    const opacity = useTransform(
        smoothProgress,
        [inAt, inAt + fadeIn, outAt - fadeOut, outAt],
        [0, 1, 1, 0],
    );
    const x = useTransform(
        smoothProgress,
        [inAt, inAt + fadeIn],
        side === 'left' ? [-20, 0] : [20, 0],
    );

    return (
        <motion.div
            className={`avenbox-annotation avenbox-annotation--${side}`}
            style={{ opacity, x }}
        >
            <p className="avenbox-annotation__tag">{tag}</p>
            <p className="avenbox-annotation__label">{label}</p>
            <div className="avenbox-annotation__rule" />
            <p className="avenbox-annotation__body">{body}</p>
        </motion.div>
    );
}

function ScrollHint({ smoothProgress }: { smoothProgress: MotionValue<number> }) {
    const opacity = useTransform(smoothProgress, [0, 0.04], [1, 0]);
    return (
        <motion.div className="avenbox-scroll-hint" style={{ opacity }}>
            <span className="avenbox-scroll-hint__text">SCROLL</span>
            <div className="avenbox-scroll-hint__line" />
        </motion.div>
    );
}

function ProgressBar({ smoothProgress }: { smoothProgress: MotionValue<number> }) {
    const scaleY = useTransform(smoothProgress, [0, 1], [0, 1]);
    return (
        <motion.div
            className="avenbox-progress-bar"
            style={{ scaleY, transformOrigin: 'top' }}
        />
    );
}

function SceneLabel({ smoothProgress }: { smoothProgress: MotionValue<number> }) {
    const opacity = useTransform(smoothProgress, [0, 0.06, 0.94, 1], [0, 1, 1, 0]);
    return (
        <motion.div className="avenbox-scene-label" style={{ opacity }}>
            <span className="avenbox-scene-label__eyebrow">AvenBox — Hardware</span>
            <span className="avenbox-scene-label__stage">PROTOTYPE</span>
        </motion.div>
    );
}

// ── Main scene ─────────────────────────────────────────────────────────────
export default function AvenBoxScene() {
    const stickyRef   = useRef<HTMLDivElement>(null);
    const scrollState = useRef<AvenBoxScrollState>({ rotY: 0, rotX: 0.04, xray: 0, vent: 0 });

    // Motion value for annotation opacity (driven from GSAP onUpdate → animate())
    const smoothProgress = useMotionValue<number>(0);

    useGSAP(() => {
        const el = stickyRef.current;
        if (!el) return;

        ScrollTrigger.create({
            trigger:    el,
            pin:        true,
            pinSpacing: true,
            start:      'top top',
            end:        '+=700%',   // 7× viewport — holds ~1 viewport each, less pin drag
            onUpdate: (self) => {
                const p = self.progress;
                scrollState.current = computeScrollState(p);
                animate(smoothProgress, p, { duration: 0, ease: 'linear' });
            },
        });
    }, { scope: stickyRef });

    return (
        // The outer wrapper — GSAP adds pin-spacer inside here automatically
        <div className="avenbox-scene">
            <div ref={stickyRef} className="avenbox-scene__sticky">

                {/* Three.js canvas */}
                <Canvas
                    camera={{ position: [0, 0, 16], fov: 42 }}
                    dpr={[1, 1.5]}
                    gl={{ antialias: true, alpha: true, powerPreference: 'high-performance' }}
                    style={{ position: 'absolute', inset: 0 }}
                >
                    {/* ── Studio 4-point lighting ─────────────────────────── */}
                    {/* Ambient fill — very low, preserves deep shadows */}
                    <ambientLight intensity={0.12} />
                    {/* Key light — top-right-front, warm white, primary shading */}
                    <directionalLight position={[5, 9, 7]}   intensity={7.5} color="#ffffff" />
                    {/* Fill light — left side, cool, softens shadows */}
                    <directionalLight position={[-6, 2, 4]}  intensity={2.8} color="#d8e8ff" />
                    {/* Rim light — above-behind, carves aluminum edge from bg */}
                    <directionalLight position={[0, 6, -10]} intensity={4.5} color="#ffffff" />
                    {/* Ground bounce — neutral cool, no warm tint on chassis */}
                    <directionalLight position={[0, -7, 3]}  intensity={0.6} color="#d0d8e8" />

                    <Suspense fallback={null}>
                        <group position={[0, -0.5, 0]}>
                            <AvenBoxLite scrollState={scrollState} />
                        </group>
                    </Suspense>
                </Canvas>

                {/* HTML overlays */}
                <SceneLabel    smoothProgress={smoothProgress} />
                <ProgressBar   smoothProgress={smoothProgress} />
                <ScrollHint    smoothProgress={smoothProgress} />

                {ANNOTATIONS.map((ann, i) => (
                    <Annotation key={i} smoothProgress={smoothProgress} {...ann} />
                ))}

            </div>
        </div>
    );
}
